import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Building2, FileCheck, Lock, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/**
 * ApiApplicationPage (`/api-application`) — Part 4. Replaces the frozen
 * self-serve `/for-brokers` signup with a reviewed application: Persona,
 * Entity Verification, IP Protection, Regulatory Affirmation, ToS/NDA.
 * No account is created here — POST /api/api-applications just queues a
 * submission for admin review; onboarding onto the Part 3 metered billing
 * engine happens after approval.
 */
const APPLICANT_TYPES = [
    { value: "broker", label: "CMA-regulated broker" },
    { value: "education", label: "Education institution" },
    { value: "fintech", label: "Fintech / API partner" },
    { value: "other", label: "Other organisation" },
];

const EMPTY = {
    applicant_type: "broker",
    full_name: "", email: "", phone: "", company_name: "", role_title: "",
    registration_number: "", regulatory_body: "", country: "", website: "", expected_mau: "",
    use_case_description: "",
    agrees_ip_terms: false, regulatory_compliance_confirmed: false, agrees_tos: false, agrees_nda: false,
};

export default function ApiApplicationPage() {
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!(form.agrees_ip_terms && form.regulatory_compliance_confirmed && form.agrees_tos && form.agrees_nda)) {
            toast.error("Please accept all affirmations before submitting.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/api-applications", form);
            setSubmitted(true);
            toast.success("Application submitted — our team will review it shortly.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
                <div className="max-w-md text-center" data-testid="api-application-success">
                    <FileCheck className="w-10 h-10 text-primary mx-auto mb-4" />
                    <h1 className="text-2xl font-extrabold mb-2">Application received</h1>
                    <p className="text-sm text-muted-foreground mb-6">
                        Thank you — our team will review your application and reach out at{" "}
                        <span className="font-semibold text-foreground">{form.email}</span> within 2 business days.
                    </p>
                    <Link to="/" className="text-primary underline text-sm" data-testid="api-application-back-home">
                        Back to home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/" className="font-extrabold tracking-tight text-lg" data-testid="api-application-logo">
                        Melian Dialogue
                    </Link>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-accent" /> Obondo API Application
                    </div>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">
                    Institutional · API access
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
                    Apply for Obondo API access
                </h1>
                <p className="text-muted-foreground leading-relaxed mb-8 max-w-2xl">
                    Institutional and API access is by application — our team reviews every submission for
                    regulatory fit and IP protection before onboarding onto the Obondo Standard / Growth / Scale
                    metered billing engine. Existing agencies can continue to log in as normal.
                </p>

                <form onSubmit={submit} className="space-y-6" data-testid="api-application-form">
                    <FormSection icon={Building2} title="Who's applying">
                        <div className="grid md:grid-cols-2 gap-4">
                            <SelectField label="Applicant type" value={form.applicant_type} onChange={setF("applicant_type")}
                                options={APPLICANT_TYPES} testId="api-app-applicant-type" />
                            <TF label="Full name" value={form.full_name} onChange={setF("full_name")} testId="api-app-full-name" />
                            <TF label="Email" type="email" value={form.email} onChange={setF("email")} testId="api-app-email" />
                            <TF label="Phone" value={form.phone} onChange={setF("phone")} testId="api-app-phone" />
                            <TF label="Company / institution name" value={form.company_name} onChange={setF("company_name")} testId="api-app-company" />
                            <TF label="Your role / title" value={form.role_title} onChange={setF("role_title")} required={false} testId="api-app-role" />
                        </div>
                    </FormSection>

                    <FormSection icon={Landmark} title="Entity verification">
                        <div className="grid md:grid-cols-2 gap-4">
                            <TF label="Registration / licence number" value={form.registration_number} onChange={setF("registration_number")} testId="api-app-reg-no" />
                            <TF label="Regulatory body (e.g. CMA, MoE)" value={form.regulatory_body} onChange={setF("regulatory_body")} required={false} testId="api-app-reg-body" />
                            <TF label="Country of registration" value={form.country} onChange={setF("country")} testId="api-app-country" />
                            <TF label="Website" value={form.website} onChange={setF("website")} required={false} testId="api-app-website" />
                            <TF label="Expected monthly active users (MAU)" value={form.expected_mau} onChange={setF("expected_mau")} required={false} testId="api-app-expected-mau" />
                        </div>
                    </FormSection>

                    <FormSection icon={Lock} title="Use case & IP protection">
                        <Label className="block text-xs font-semibold text-muted-foreground mb-1">
                            Describe your intended use case
                        </Label>
                        <Textarea
                            value={form.use_case_description}
                            onChange={(e) => setF("use_case_description")(e.target.value)}
                            rows={4}
                            required
                            data-testid="api-app-use-case"
                            placeholder="How will you use the Obondo API — trading floor rollout, classroom tool, fintech integration, etc.?"
                        />
                        <CheckField
                            label="I agree that Melian Dialogue's IP, models, and methodology remain proprietary and may not be reverse-engineered or resold."
                            checked={form.agrees_ip_terms} onChange={setF("agrees_ip_terms")} testId="api-app-ip-terms"
                        />
                    </FormSection>

                    <FormSection icon={ShieldCheck} title="Regulatory affirmation & agreements">
                        <CheckField
                            label="I affirm that my organisation is properly licensed/registered for its stated activity in its jurisdiction of operation."
                            checked={form.regulatory_compliance_confirmed} onChange={setF("regulatory_compliance_confirmed")} testId="api-app-regulatory"
                        />
                        <CheckField
                            label="I agree to the Obondo API Terms of Service."
                            checked={form.agrees_tos} onChange={setF("agrees_tos")} testId="api-app-tos"
                        />
                        <CheckField
                            label="I agree to sign a mutual Non-Disclosure Agreement (NDA) prior to receiving production API keys."
                            checked={form.agrees_nda} onChange={setF("agrees_nda")} testId="api-app-nda"
                        />
                    </FormSection>

                    <Button type="submit" disabled={busy} data-testid="api-app-submit" className="w-full py-2.5 font-semibold">
                        {busy ? "Submitting…" : "Submit application"}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                        Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="api-app-login-link">Sign in</Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

function FormSection({ icon: Icon, title, children }) {
    return (
        <div className="border border-border bg-card rounded-lg p-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-primary" />
                <div className="overline">{title}</div>
            </div>
            {children}
        </div>
    );
}

function TF({ label, value, onChange, type = "text", testId, required = true }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">{label}</span>
            <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </label>
    );
}

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">{label}</span>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>
                    {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
            </Select>
        </label>
    );
}

function CheckField({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
            <span className="text-xs text-muted-foreground leading-relaxed">{label}</span>
        </label>
    );
}
